import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Sender ID validation: 3-11 alphanumeric characters only, no spaces.
// Stored lowercase; the regex accepts mixed case so users can type freely
// while normalizeSenderIdCase lowercases on submit.
export const SENDER_ID_REGEX = /^[a-zA-Z0-9]{3,11}$/;
export const SENDER_ID_PLACEHOLDER = "yourbrand";
export const SENDER_ID_MIN_LENGTH = 3;
export const SENDER_ID_MAX_LENGTH = 11;

// Mirror of api/src/shared/sender-id.ts RESERVED_SENDER_IDS. Keep both lists
// in sync. The server is the source of truth, and this copy lets us reject
// reserved IDs inline before the user wastes a network round-trip.
export const RESERVED_SENDER_IDS = new Set<string>([
  'admin', 'api', 'app', 'auth', 'login', 'logout', 'signup', 'register',
  'support', 'help', 'docs', 'status', 'health', 'metrics', 'public',
  'static', 'assets', 'cdn', 'mail', 'email', 'webhook', 'webhooks',
  'callback', 'oauth', 'sms', 'review', 'reviews', 'r', 's', 'qr',
  'www', 'reputationguard', 'repguard', 'seedhub', 'rev',
  'test', 'demo', 'example', 'null', 'undefined',
]);

export function isValidSenderId(value: string): boolean {
  return SENDER_ID_REGEX.test(value) && !RESERVED_SENDER_IDS.has(value.trim().toLowerCase());
}

/**
 * Which rule a sender ID broke, and the values its message needs to quote.
 *
 * Deliberately NOT a rendered string. This function used to return English
 * prose, which meant a customer running the app in Greek read a fully Greek
 * dialog with an English sentence under the field. The rule that was broken is
 * language-free; only the sentence describing it is not, so the sentence is the
 * caller's to produce. `lib/sender-id-message.ts` is the one place that turns a
 * problem into text, and every call site goes through it.
 */
export type SenderIdProblem =
  | { reason: 'required' }
  | { reason: 'tooShort'; min: number }
  | { reason: 'tooLong'; max: number }
  | { reason: 'charset' }
  | { reason: 'reserved'; value: string };

/**
 * Validate a sender ID and return the problem found, or null when valid.
 * Use for inline form validation so the user sees *why* their input was
 * rejected, rather than watching it be silently normalized.
 *
 * The rules here are mirrored by api/src/shared/sender-id.ts and pinned in both
 * workspaces by sender-id.equivalence.test.ts. The thresholds, the order of the
 * checks and the trimming are part of that contract: change the wording of a
 * message freely, and change a rule only on both sides at once.
 */
export function validateSenderId(value: string): SenderIdProblem | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return { reason: 'required' };
  if (trimmed.length < SENDER_ID_MIN_LENGTH) return { reason: 'tooShort', min: SENDER_ID_MIN_LENGTH };
  if (trimmed.length > SENDER_ID_MAX_LENGTH) return { reason: 'tooLong', max: SENDER_ID_MAX_LENGTH };
  if (!/^[a-zA-Z0-9]+$/.test(trimmed)) return { reason: 'charset' };
  if (RESERVED_SENDER_IDS.has(trimmed.toLowerCase())) return { reason: 'reserved', value: trimmed };
  return null;
}

/** Normalize a (validated) sender ID to its canonical lowercase form. */
export function normalizeSenderId(value: string): string {
  return value.trim().toLowerCase();
}

/*
 * `capitalizeWords` was here, and it is deliberately gone rather than left for a
 * future caller to find.
 *
 * Its only two consumers were the CSV importer's name fields, where it
 * lowercased the whole string and re-capitalised each word: `McDonald` became
 * `Mcdonald`, `O'BRIEN` became `O'brien`, and `ΜΑΡΙΑ` became `Μαρια`, a
 * misspelling, because Greek writes all-caps without accents. A helper that
 * corrupts real names is not one to keep around for the next person who wants
 * to tidy up a text field.
 */
